/* The ORBIS emblem.
 *
 * ## Inline, not an imported file
 *
 * Vite would happily turn `orbis-mark.svg` into a URL, and the request would be same-origin, so
 * `CON-01` is not the objection. The objections are smaller and both real: an `<img>` cannot take
 * its colour from the surrounding UI, and one more request means one more thing that can be missing
 * from a stale nginx cache while the rest of the bundle is current — a logo that intermittently
 * fails to draw looks like a broken deployment.
 *
 * ## Why the gradient id is derived from a prop
 *
 * SVG ids are document-global. Two of these on one page with a hardcoded `id="g"` and the second
 * silently paints with the first one's gradient — which is invisible here, because both would look
 * correct, right up until one of them is given different stops. The id carries the instance name.
 *
 * The artwork is the emblem from `site/logo/orbis-logo.svg` with the wordmark removed: beside it the
 * product name is live text in Inter, so a baked wordmark would put a second typeface in the
 * command bar.
 */

interface Props {
  /** Rendered size in px, square. */
  size?: number
  /** Distinguishes this instance's gradient id from any other on the page. */
  idSuffix?: string
  className?: string
}

export function OrbisMark({ size = 18, idSuffix = 'deck', className }: Props) {
  const gradient = `orbis-mark-${idSuffix}`
  return (
    <svg
      width={size}
      height={size}
      viewBox="42 27 246 246"
      className={className}
      role="img"
      aria-label="ORBIS"
    >
      <defs>
        <linearGradient id={gradient} x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#008DEB" />
          <stop offset="1" stopColor="#65D7FF" />
        </linearGradient>
      </defs>
      <g
        transform="translate(30 20)"
        fill="none"
        stroke={`url(#${gradient})`}
        strokeLinecap="round"
      >
        <circle cx="135" cy="130" r="82" strokeWidth="18" />
        <ellipse
          cx="135"
          cy="130"
          rx="116"
          ry="43"
          transform="rotate(-12 135 130)"
          strokeWidth="15"
        />
        <ellipse
          cx="135"
          cy="130"
          rx="48"
          ry="91"
          transform="rotate(25 135 130)"
          strokeWidth="13"
        />
        <path
          d="M74 77c38-29 89-34 132-9M66 181c42 31 99 34 143 5"
          strokeWidth="11"
        />
      </g>
    </svg>
  )
}
